/**
 * Pure matcher for the listing-import wizard (TNK-112).
 *
 * Given a remote eBay listing (its sku + title), rank local products by
 * how likely they're the same thing. SKU is a perfect match when this app
 * created the listing (createEbayListing stamps SKU = productId / cardId);
 * otherwise we fall back to a token-overlap score on the title.
 *
 * Kept dependency-free + framework-free so it can be vitested in
 * isolation and run synchronously inside the wizard's render.
 */

export interface MinimalProduct {
  id: string;
  name?: string;
  listingTitle?: string;
  archivedAt?: string;
}

export interface RemoteListingForMatch {
  /** SKU stamped on the eBay listing. When this app created the listing,
   *  SKU = the local product/card id. */
  sku?: string;
  /** Listing title as shown on eBay. */
  title?: string;
}

export interface ProductMatch {
  productId: string;
  name: string;
  /** 0–100. 100 = SKU exact match; otherwise the share of meaningful
   *  title tokens the product carries. */
  score: number;
}

const STOP_TOKENS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "of",
  "for",
  "with",
  "to",
  "in",
  "on",
  "at",
  "by",
  "from",
  "tcg",
  "pokemon",
  "pokémon",
  "card",
  "cards",
  "lot",
  "bundle",
  "pack",
  "packs",
  "deck",
  "decks",
  "new",
  "nm",
  "lp",
  "mp",
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOP_TOKENS.has(t));
}

/**
 * Return up to N candidate products for a remote listing, sorted by
 * descending match score. Archived products are excluded.
 */
export function findProductMatches(
  remote: RemoteListingForMatch,
  products: MinimalProduct[],
): ProductMatch[] {
  const active = products.filter((p) => !p.archivedAt);

  // SKU exact match wins — this is the path for listings tcg-web
  // originated, where SKU is literally the product id.
  if (remote.sku) {
    const exact = active.find((p) => p.id === remote.sku);
    if (exact) {
      return [
        {
          productId: exact.id,
          name: exact.name ?? exact.listingTitle ?? exact.id,
          score: 100,
        },
      ];
    }
  }

  const remoteTokens = new Set(tokenize(remote.title ?? ""));
  if (remoteTokens.size === 0) return [];

  const scored: ProductMatch[] = [];
  for (const p of active) {
    const candidateText = `${p.name ?? ""} ${p.listingTitle ?? ""}`;
    const productTokens = new Set(tokenize(candidateText));
    if (productTokens.size === 0) continue;

    let overlap = 0;
    for (const t of remoteTokens) {
      if (productTokens.has(t)) overlap += 1;
    }
    if (overlap === 0) continue;
    // Score = fraction of the remote title's tokens the product covers.
    // Anchoring on the remote (not the union) means a long product title
    // with one matching token doesn't beat a short product title that
    // covers the listing exactly.
    const score = Math.round((overlap / remoteTokens.size) * 100);
    scored.push({
      productId: p.id,
      name: p.name ?? p.listingTitle ?? p.id,
      score,
    });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored;
}
